import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { currentSelectedLanguage } from "../../../../StateManagement/actions";
import "./GridGuardOptions.css";


export const formatLanguageText = (template, ...args) =>
  (template || "").replace(/\{(\d+)\}/g, (match, index) =>
    args[index] !== undefined ? args[index] : match
  );

export const isReaderEditor = (options, email) => {
  if (!options.allowEdit || options.editMode === "none") return false;
  if (options.editMode === "all") return true;
  return !!options.authorizedEditors?.includes(email);
};

export const isReaderHistoryEnabled = (options, email) => {
  if (!options.showHistory) return false;
  if (options.historyVisibility === "all") return true;
  if (options.historyVisibility === "editorsOnly") {
    return isReaderEditor(options, email);
  }
  return !!options.authorizedHistoryViewers?.includes(email);
};

export const getEditEnabledCount = (options, readers) =>
  readers.filter((email) => isReaderEditor(options, email)).length;

export const getHistoryEnabledCount = (options, readers) =>
  readers.filter((email) => isReaderHistoryEnabled(options, email)).length;
function AllowEditingPopup({ isOpen, options, readers, onClose, onSave }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const [localOptions, setLocalOptions] = useState(options);
  const [readerSearch, setReaderSearch] = useState("");

  useEffect(() => {
    if (isOpen) {
      setLocalOptions({
        ...options,
        showHistory: true,
        historyVisibility: options.showHistory ? options.historyVisibility || "all" : "all",
      });
      setReaderSearch("");
    }
  }, [isOpen, options]);

  const availableReaders = useMemo(() => Array.from(new Set(readers || [])), [readers]);

  const filteredReaders = availableReaders.filter((email) =>
    email.toLowerCase().includes(readerSearch.toLowerCase())
  );

  const editEnabledCount = getEditEnabledCount(localOptions, availableReaders);
  const historyEnabledCount = getHistoryEnabledCount(localOptions, availableReaders);

  const isHistorySpecific =
    localOptions.historyVisibility === "specific" ||
    localOptions.historyVisibility === "editorsOnly";

  const showReaderList = localOptions.editMode === "specific" || isHistorySpecific;
  const hasNoSpecificEditors = localOptions.editMode === "specific" && editEnabledCount === 0;
  const hasNoHistoryReaders = isHistorySpecific && historyEnabledCount === 0;
  const isSaveBlocked = (showReaderList && availableReaders.length === 0) || (hasNoSpecificEditors && hasNoHistoryReaders);
  const handleEditModeChange = (mode) => {
    setLocalOptions((prev) => ({
      ...prev,
      editMode: mode,
      // Switching from "all" to "specific" keeps the current editors instead of emptying the list.
      authorizedEditors:
        mode === "specific" && prev.editMode === "all"
          ? [...availableReaders]
          : prev.authorizedEditors,
    }));
  };

  const handleHistoryAllClick = () => {
    setLocalOptions((prev) => ({
      ...prev,
      showHistory: true,
      historyVisibility: "all",
      authorizedHistoryViewers: [...availableReaders],
    }));
  };

  const handleHistorySpecificClick = () => {
    setLocalOptions((prev) => ({
      ...prev,
      showHistory: true,
      historyVisibility: "specific",
      // Keep whoever can currently see history instead of emptying the list.
      authorizedHistoryViewers: availableReaders.filter((email) => isReaderHistoryEnabled(prev, email)),
    }));
  };

  const handleToggleEditor = (email) => {
    setLocalOptions((prev) => {
      const currentEditors =
        prev.editMode === "all"
          ? [...availableReaders]
          : (prev.authorizedEditors || []).filter((item) => availableReaders.includes(item));
      const nextEditors = currentEditors.includes(email)
        ? currentEditors.filter((item) => item !== email)
        : [...currentEditors, email];
      return { ...prev, allowEdit: true, editMode: "specific", authorizedEditors: nextEditors };
    });
  };

  const handleToggleHistory = (email) => {
    setLocalOptions((prev) => {
      const currentViewers = availableReaders.filter((email) => isReaderHistoryEnabled(prev, email));
      const nextViewers = currentViewers.includes(email)
        ? currentViewers.filter((item) => item !== email)
        : [...currentViewers, email];
      return {
        ...prev,
        showHistory: true,
        historyVisibility: "specific",
        authorizedHistoryViewers: nextViewers,
      };
    });
  };

  const handleBatchHistory = (mode) => {
    setLocalOptions((prev) => {
      if (mode === "disableAll") {
        return { ...prev, showHistory: true, historyVisibility: "specific", authorizedHistoryViewers: [] };
      }
      if (mode === "enableEditors") {
        return {
          ...prev,
          showHistory: true,
          historyVisibility: "specific",
          authorizedHistoryViewers: availableReaders.filter((email) => isReaderEditor(prev, email)),
        };
      }
      return {
        ...prev,
        showHistory: true,
        historyVisibility: "all",
        authorizedHistoryViewers: [...availableReaders],
      };
    });
  };

  const handleSave = () => {
    if (isSaveBlocked) return;
    const next = { ...localOptions };

    next.authorizedEditors = (next.authorizedEditors || []).filter((email) => availableReaders.includes(email));
    next.authorizedHistoryViewers = (next.authorizedHistoryViewers || []).filter((email) =>
      availableReaders.includes(email)
    );

    // "Specific readers" with nobody picked means nothing is granted, so switch it off.
    if (next.editMode === "specific" && editEnabledCount === 0) {
      next.allowEdit = false;
      next.editMode = "none";
    }

    onSave(next);
  };

  if (!isOpen) return null;

  return (
    <div className="gg-modal-overlay gg-modal-overlay-top" id="ggAllowEditingPopup">
      <div className="gg-modal-content gg-modal-sm">
        {/* Header */}
        <div className="gg-modal-header">
          <div className="gg-header-left">
            <div className="gg-header-icon">
              <span className="material-icons">edit</span>
            </div>
            <h4 className="gg-title">{selectedLanguage.LstLanguageCodes.UI_GG_AllowEditingSettings}</h4>
          </div>
          <button type="button" className="gg-close-btn" onClick={onClose} title={selectedLanguage.LstLanguageCodes.UI_Close}>
            <span className="material-icons">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="gg-modal-body">
          {/* 1. Edit permissions */}
          <div className="gg-section">
            <div className="gg-section-head">
              <p className="gg-section-title">{selectedLanguage.LstLanguageCodes.UI_GG_EditPermissions}</p>
              <span className="gg-chip gg-chip-green">
                {localOptions.editMode === "all"
                  ? selectedLanguage.LstLanguageCodes.UI_GG_AllReadersCanEdit
                  : formatLanguageText(
                    selectedLanguage.LstLanguageCodes.UI_GG_SpecificEditorsCount,
                    editEnabledCount
                  )}
              </span>
            </div>
            <div className="gg-segment">
              <button
                type="button"
                className={`gg-segment-btn ${localOptions.editMode === "all" ? "gg-active-dark" : ""}`}
                onClick={() => handleEditModeChange("all")}>
                {selectedLanguage.LstLanguageCodes.UI_GG_AllReaders}
              </button>
              <button
                type="button"
                className={`gg-segment-btn ${localOptions.editMode === "specific" ? "gg-active-dark" : ""}`}
                onClick={() => handleEditModeChange("specific")}>
                {selectedLanguage.LstLanguageCodes.UI_GG_SpecificReaders}
              </button>
            </div>
          </div>

          {/* 2. Per-reader access control (only when something is "Specific") */}
          {showReaderList && (
            <div className="gg-section">
              <div className="gg-section-head">
                <div>
                  <p className="gg-section-title">{selectedLanguage.LstLanguageCodes.UI_GG_PerReaderAccessControl}</p>
                  <p className="gg-section-desc">{selectedLanguage.LstLanguageCodes.UI_GG_PerReaderAccessControlDesc}</p>
                </div>
                <div className="gg-chip-row">
                  <span className="gg-chip gg-chip-green">
                    {formatLanguageText(selectedLanguage.LstLanguageCodes.UI_GG_EditCount, editEnabledCount)}
                  </span>
                  <span className="gg-chip gg-chip-indigo">
                    {formatLanguageText(selectedLanguage.LstLanguageCodes.UI_GG_HistoryCount, historyEnabledCount)}
                  </span>
                </div>
              </div>

              <div className="gg-reader-box">
                <div className="gg-reader-toolbar">
                  <div className="gg-search-wrap">
                    <input
                      type="text"
                      className="gg-search-input"
                      placeholder={selectedLanguage.LstLanguageCodes.UI_GG_SearchReaders}
                      value={readerSearch}
                      onChange={(e) => setReaderSearch(e.target.value)}
                    />
                    {readerSearch && (
                      <span className="material-icons gg-search-clear" onClick={() => setReaderSearch("")}>
                        close
                      </span>
                    )}
                  </div>
                  <div className="gg-chip-row">
                    <button
                      type="button"
                      className="gg-batch-btn gg-batch-indigo"
                      title={selectedLanguage.LstLanguageCodes.UI_GG_HistoryEqualsEditorsTip}
                      onClick={() => handleBatchHistory("enableEditors")}>
                      {selectedLanguage.LstLanguageCodes.UI_GG_HistoryEqualsEditors}
                    </button>
                    <button
                      type="button"
                      className="gg-batch-btn gg-batch-grey"
                      title={selectedLanguage.LstLanguageCodes.UI_GG_ClearHistoryTip}
                      onClick={() => handleBatchHistory("disableAll")}>
                      {selectedLanguage.LstLanguageCodes.UI_GG_ClearHistory}
                    </button>
                  </div>
                </div>

                <div className="gg-reader-list">
                  {filteredReaders.length === 0 && (
                    <p className="gg-empty-text">{selectedLanguage.LstLanguageCodes.UI_NoReadersFound}</p>
                  )}
                  {filteredReaders.map((email) => {
                    const canEdit = isReaderEditor(localOptions, email);
                    const canViewHistory = isReaderHistoryEnabled(localOptions, email);
                    return (
                      <div
                        key={email}
                        className={`gg-reader-row ${canEdit || canViewHistory ? "gg-reader-active" : ""}`}>
                        <span className="gg-reader-email" title={email}>
                          {email}
                        </span>
                        <div className="gg-reader-actions">
                          <button
                            type="button"
                            className={`gg-perm-btn ${canEdit ? "gg-perm-edit-on" : ""}`}
                            title={canEdit ? selectedLanguage.LstLanguageCodes.UI_GG_EditOnTip : selectedLanguage.LstLanguageCodes.UI_GG_EditOffTip}
                            onClick={() => handleToggleEditor(email)}>
                            <span className="material-icons">edit</span>
                            <span>{canEdit ? selectedLanguage.LstLanguageCodes.UI_GG_EditOn : selectedLanguage.LstLanguageCodes.UI_GG_EditOff}</span>
                          </button>
                          <button
                            type="button"
                            className={`gg-perm-btn ${canViewHistory ? "gg-perm-history-on" : ""}`}
                            title={
                              canViewHistory ? selectedLanguage.LstLanguageCodes.UI_GG_HistoryOnTip : selectedLanguage.LstLanguageCodes.UI_GG_HistoryOffTip
                            }
                            onClick={() => handleToggleHistory(email)}>
                            <span className="material-icons">history</span>
                            <span>
                              {canViewHistory ? selectedLanguage.LstLanguageCodes.UI_GG_HistoryOn : selectedLanguage.LstLanguageCodes.UI_GG_HistoryOff}
                            </span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 3. Change history (always rendered; sits BELOW the reader box when it is shown) */}
          <div className="gg-section">
            <div className="gg-section-head">
              <p className="gg-section-title">{selectedLanguage.LstLanguageCodes.UI_GG_ShowChangeHistory}</p>
              <span className="gg-chip gg-chip-indigo">
                {localOptions.historyVisibility === "all"
                  ? selectedLanguage.LstLanguageCodes.UI_GG_AllReaders
                  : formatLanguageText(selectedLanguage.LstLanguageCodes.UI_GG_ReadersEnabledCount, historyEnabledCount)}
              </span>
            </div>
            <div className="gg-segment">
              <button
                type="button"
                id="btnGridGuardHistoryAll"
                className={`gg-segment-btn ${localOptions.historyVisibility === "all" ? "gg-active-dark" : ""}`}
                onClick={handleHistoryAllClick}>
                {selectedLanguage.LstLanguageCodes.UI_GG_AllReaders}
              </button>
              <button
                type="button"
                id="btnGridGuardHistorySpecific"
                className={`gg-segment-btn ${isHistorySpecific ? "gg-active-dark" : ""}`}
                onClick={handleHistorySpecificClick}>
                {selectedLanguage.LstLanguageCodes.UI_GG_SpecificReaders}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="gg-modal-footer">
          <div className="gg-footer-actions">
            <button type="button" className="gg-btn gg-btn-cancel" onClick={onClose}>
              {selectedLanguage.LstLanguageCodes.UI_Cancel}
            </button>
            <button
              type="button"
              className="gg-btn gg-btn-primary"
              onClick={handleSave}
              disabled={isSaveBlocked}
              title={showReaderList && availableReaders.length === 0 ? selectedLanguage.LstLanguageCodes.UI_NoReadersFound : undefined}>
              {selectedLanguage.LstLanguageCodes.UI_Save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AllowEditingPopup;